import { faComment, faArrowTurnDownRight, faUser, faUserGroup, faXmark, faUpFromLine, faBagShopping, faFileArrowUp } from '@fortawesome/pro-light-svg-icons'
import { CustomerLogTypeKeys } from '@teinor/erp/types/company/customer/customerLog'
import { QuoteOrderLogTypeKeys } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderLog'
import { useCallback, useState, useEffect, Fragment } from 'react'
import { useParams } from 'react-router'
import { ActivityDisplayComponent } from '../../../baseComponents/ActivityDisplay'
import { UserPermissionsSingle } from '../../../baseComponents/CreateRoutering/types'
import { CreateTwinComment, TwinCommentBox } from '../../../baseComponents/TwinComment'
import TwinTrans from '../../../baseComponents/TwinTrans'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { CustomerActivityTy, CustomerActivityTypeTy } from '../CustomerProfileTabs/CustomerNotes/types'
import PermissionChecker from '../../../baseComponents/PermissionChecker'
import { VirtualListHeightAutosized } from '../../../baseComponents/TwinTable/VirtualTableListing'
import { VirtualListHeightAutosizedInstanceType } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import NullComponent from '../../../baseComponents/NullComponent'
import { projectTypesInversed, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { SellOrderLogTypeKeys } from '@teinor/erp/types/company/order/sell/sellOrder/sellOrderLog'
import { SellDeliveryNoteLogTypeKeys } from '@teinor/erp/types/company/order/sell/sellDeliveryNote/sellDeliveryNoteLog'
import { SellInvoiceLogTypeKeys } from '@teinor/erp/types/company/order/sell/sellInvoice/sellInvoiceLog'

interface CustomerActivityItemProps {
    item: CustomerActivityTy
    getData: () => Promise<void>
}

interface CustomerComponentProps {
    item: CustomerActivityTy
    getData: () => Promise<void>
    VirtualListHeightAutosizedInstance?: VirtualListHeightAutosizedInstanceType
}

const CustomerActivityItem: React.FC<CustomerActivityItemProps> = ({ item, ...rest }) => {
    const componentsTypeDict: Record<CustomerActivityTypeTy, React.FC<CustomerComponentProps>> = {
        'createdAt': NullComponent,
        'comment': CustomerProjectCommentComponent,
        'customerComments': CustomerCommentComponent,
        'customerCommentsCampaigns': CustomerCommentCampaignComponent,
        'customerCampaignStatus': CustomerCampaignHistoryComponent,
        'status': CustomerProjectHistoryComponent,
        'assignedEmployee': CustomerProjectEmployeeComponent,
        'assignedGroup': CustomerProjectGroupComponent,
        'deletedEmployee': CustomerProjectEmployeeDeletedComponent,
        'deletedGroup': CustomerProjectGroupDeletedComponent,
        'changedType': CustomerProjectChangedTypeComponent,
        'disabledProject': CustomerProjectDisabledComponent,
        'reactivatedProject': CustomerProjectReactiveComponent,
        'creatingQuoteOrder': CustomerProjectCreatedQuoteOrderComponent,
        'newVersionQuoteOrder': CustomerProjectNewVersionQuoteOrderComponent,
        'waitingResponseQuoteOrder': CustomerProjectWaitingResponseQuoteOrderComponent,
        'acceptedQuoteOrder': CustomerProjectAcceptedQuoteOrderComponent,
        'deniedQuoteOrder': CustomerProjectDeniedQuoteOrderComponent,
        'newVersionCustomerQuoteOrder': CustomerProjectNewVersionCustomerQuoteOrderComponent,
        'acceptedCustomerQuoteOrder': CustomerProjectAcceptedCustomerQuoteOrderComponent,
        'deniedCustomerQuoteOrder': CustomerProjectDeniedCustomerQuoteOrderComponent,
        'createCustomer': CustomerCreateLogComponent,
        'updateCustomer': CustomerUpdateLogComponent,
        'unactiveCustomer': CustomerUnactiveLogComponent,
        'reactiveCustomer': CustomerReactiveLogComponent,
        'sent': CustomerProjectSentCustomerQuoteOrderComponent,
        //aqui
        'processed': CustomerProjectSentCustomerQuoteOrderComponent,
        'turnedProject': CustomerProjectOpTurnedProjectComponent,
        'creatingSellOrder': CustomerProjectCreatedSellOrderComponent,
        'waitingResponseSellOrder': CustomerProjectWaitingResponseSellOrderComponent,
        'processedSellOrder': CustomerProjectProcessedSellOrderComponent,
        'creatingSellDeliveryNote': CustomerProjectCreatedSellDeliveryNoteComponent,
        'waitingResponseSellDeliveryNote': CustomerProjectWaitingResponseSellDeliveryNoteComponent,
        'processedSellDeliveryNote': CustomerProjectProcessedSellDeliveryNoteComponent,
        'refundedSellDeliveryNote': CustomerProjectRefundedSellDeliveryNoteComponent,
        'creatingSellInvoice': CustomerProjectCreatedSellInvoiceComponent,
        'waitingResponseSellInvoice': CustomerProjectWaitingResponseSellInvoiceComponent,
        'acceptedSellInvoice': CustomerProjectAcceptedSellInvoiceComponent,
        'deniedSellInvoice': CustomerProjectDeniedSellInvoiceComponent,
        'refundedSellInvoice': CustomerProjectRefundedSellInvoiceComponent
    }
    const Component = componentsTypeDict[item.type]
    return (
        <Fragment>
            <div className='activity_display_line'></div>
            <Component item={item} {...rest} />
        </Fragment>
    )
}

const CustomerProjectCommentComponent: React.FC<CustomerComponentProps> = ({ item, getData }) => {
    const { deleteCustomerProjectComment, updateCustomerProjectComment } = useCustomerProjectCommentComponentLogic({ getData })
    const { t } = useTwinTranslation()
    const data = item?.data
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faComment} tooltipName={'commentEmployeeActivityTab'} headerText={t('commentInProjectOpp', 'Comentario en ') + (data?.projectType === projectTypesInversed['project'] ? 'el proyecto' : 'la oportunidad') + ': ' + item?.data?.CustomerProject?.name} image={item.data.Employee?.profile_image} date={item.date} nameTrigger={item.data.Employee.fullname_short} >
            <TwinCommentBox id={item.data.id} downloadFileURL='/api/app/customer/customerProject/customerProjectComment/getCustomerProjectCommentInstanceFile' comment={item.data.message || ''} deleteComment={deleteCustomerProjectComment} posArr={item.data.id} fieldComment={'message'} updateComment={updateCustomerProjectComment} EmployeeId={item.data?.EmployeeId} file_url={item.data?.file_url} />
        </ActivityDisplayComponent>
    )
}

interface CustomerProjectCommentComponentLogicProps {
    getData: () => Promise<void>
}

const useCustomerProjectCommentComponentLogic = ({ getData }: CustomerProjectCommentComponentLogicProps) => {
    const deleteCustomerProjectComment = useCallback(async (id: number) => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/customerProjectComment/deleteCustomerProjectComment', { id })
        if (result) {
            await getData()
        }
    }, [getData])

    const updateCustomerProjectComment = useCallback(async (id: number, message: string) => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/customerProjectComment/updateCustomerProjectComment', { id, message })
        if (result) {
            await getData()
        }
    }, [getData])

    return { deleteCustomerProjectComment, updateCustomerProjectComment }
}

const CustomerCommentComponent: React.FC<CustomerComponentProps> = ({ item, getData, VirtualListHeightAutosizedInstance }) => {
    const { deleteCustomerComment, updateCustomerComment } = useCustomerCommentComponentLogic({ getData, VirtualListHeightAutosizedInstance })
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faComment} tooltipName={'commentEmployeeActivityTab'} headerText={t('commentCustomer', 'Comentario en el cliente')} image={item.data.Employee?.profile_image} date={item.date} nameTrigger={item.data.Employee.fullname_short} >
            <TwinCommentBox id={item.data.id} downloadFileURL='/api/app/customer/customerNote/getCustomerNoteInstanceFile' comment={item.data.message || ''} deleteComment={deleteCustomerComment} posArr={item.data.id} fieldComment={'message'} updateComment={updateCustomerComment} EmployeeId={item.data?.EmployeeId} file_url={item.data?.file_url} />
        </ActivityDisplayComponent>
    )
}

interface CustomerCommentComponentLogicProps {
    getData: () => Promise<void>
    VirtualListHeightAutosizedInstance?: VirtualListHeightAutosizedInstanceType
}

const useCustomerCommentComponentLogic = ({ getData, VirtualListHeightAutosizedInstance }: CustomerCommentComponentLogicProps) => {
    const deleteCustomerComment = useCallback(async (id: number) => {
        const result = await twinFetchPostJSON('/api/app/customer/customerNote/deleteCustomerNote', { id })
        if (result) {
            await getData()
        }
    }, [getData])

    const updateCustomerComment = useCallback(async (id: number, message: string) => {
        const result = await twinFetchPostJSON('/api/app/customer/customerNote/updateCustomerNote', { id, message })

        if (result) {
            await getData()
            VirtualListHeightAutosizedInstance?.resetRowHeights()
        }
    }, [getData, VirtualListHeightAutosizedInstance])

    return { deleteCustomerComment, updateCustomerComment }
}

const CustomerCommentCampaignComponent: React.FC<CustomerComponentProps> = ({ item, getData }) => {
    const { deleteCustomerCommentCampaign, updateCustomerCommentCampaign } = useCustomerCommentCampaignComponentLogic({ getData })
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faComment} tooltipName={'commentEmployeeActivityTab'} headerText={t('commentInCampaing', 'Comentario en la campaña') + ': ' + item?.data?.CampaignCustomer?.Campaign?.name} image={item.data.Employee?.profile_image} date={item.date} nameTrigger={item.data.Employee.fullname_short} >
            <TwinCommentBox id={item.data.id} downloadFileURL='/api/app/customer/campaign/customer/comment/getCampaignCustomerCommentInstanceFile' comment={item.data.message || ''} deleteComment={deleteCustomerCommentCampaign} posArr={item.data.id} fieldComment={'message'} updateComment={updateCustomerCommentCampaign} EmployeeId={item.data?.EmployeeId} file_url={item.data?.file_url} />
        </ActivityDisplayComponent>
    )
}

interface CustomerCommentCampaignComponentLogicProps {
    getData: () => Promise<void>
}

const useCustomerCommentCampaignComponentLogic = ({ getData }: CustomerCommentCampaignComponentLogicProps) => {
    const deleteCustomerCommentCampaign = useCallback(async (id: number) => {
        const result = await twinFetchPostJSON('/api/app/customer/campaign/customer/comment/deleteCampaignCustomerComment', { id })
        if (result) {
            await getData()
        }
    }, [getData])

    const updateCustomerCommentCampaign = useCallback(async (id: number, message: string) => {
        const result = await twinFetchPostJSON('/api/app/customer/campaign/customer/comment/updateCampaignCustomerComment', { id, message })
        if (result) {
            await getData()
        }
    }, [getData])

    return { deleteCustomerCommentCampaign, updateCustomerCommentCampaign }
}

const CustomerCampaignHistoryComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars, t } = useTwinTranslation()
    const campaignName = item?.data?.CampaignCustomer?.Campaign?.name
    const campaignStatus = item?.data?.CampaignStatus?.name
    const changedCampaignStatusText = tVars('changesStatusCampaignN', 'Campaña {{campaignName}}: cambio de estado a {{campaignStatus}}', { campaignName, campaignStatus })
    const deletedCampaingStatusText = t('deletedStatusCampaign', 'Se ha eliminado el estado de la campaña')
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faArrowTurnDownRight} mainColor={item?.data?.CustomerProjectStatus?.color || '#979797'} headerText={campaignStatus ? changedCampaignStatusText : deletedCampaingStatusText} image={item?.data?.Employee?.profile_image} date={item.data.createdAt} secondText={item?.data?.CustomerProjectStatus?.name} tooltipName={'statusChangedEmployeeActivityTab'} nameTrigger={item?.data?.Employee?.fullname_short} />
    )
}

const CustomerProjectHistoryComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faArrowTurnDownRight} mainColor={item?.data?.CustomerProjectStatus?.color} headerText={tVars('changesStatusProjectOppN', '{{projectType}} {{projectName}}: cambio de estado a', { projectName, projectType })} image={item?.data?.Employee?.profile_image} date={item.data.createdAt} secondText={item?.data?.CustomerProjectStatus?.name} tooltipName={'statusChangedEmployeeActivityTab'} nameTrigger={item?.data?.Employee?.fullname_short} />
    )
}

const CustomerProjectEmployeeComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={tVars('employeeAssignedToProjecOppN', '{{projectType}} {{projectName}}: asignado a', { projectName, projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Employee.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'assignedEmployeeActivityTab'} />
    )
}

const CustomerProjectGroupComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUserGroup} headerText={tVars('assignedGroupToProjectOppN', '{{projectType}} {{projectName}}: grupo asignado', { projectName, projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Group.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'assignedGroupActivityTab'} />
    )
}

const CustomerProjectEmployeeDeletedComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={tVars('removedFromProjectOppN', '{{projectType}} {{projectName}}: eliminado', { projectName, projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Employee.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'removedEmployeeActivityTab'} />
    )
}

const CustomerProjectGroupDeletedComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUserGroup} headerText={tVars('removedGroupToProjectOppN', '{{projectType}} {{projectName}}: grupo eliminado', { projectName, projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} secondText={item.data.Group.name} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'removedGroupActivityTab'} />
    )
}

const CustomerProjectChangedTypeComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faArrowTurnDownRight} mainColor={item?.data?.CustomerProjectStatus?.color} headerText={tVars('changedProjectOppTypeN', '{{projectType}} {{projectName}}: cambio de tipo a', { projectName, projectType })} image={item?.data?.EmployeeTrigger?.profile_image} date={item.data.createdAt} secondText={item?.data?.CustomerProjectType?.name} tooltipName={'changedProjectTypeActivityTab'} nameTrigger={item?.data?.EmployeeTrigger?.fullname_short} />
    )
}

const CustomerProjectDisabledComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faXmark} mainColor={'#BA435D'} headerText={tVars('disabledProjectOppN', '{{projectType}} {{projectName}} desactivado', { projectName, projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'disabledProjectActivityTab'} />
    )
}

const CustomerProjectReactiveComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    const projectType = item?.data?.projectTypeDict[item?.data?.projectType]
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUpFromLine} mainColor={'#43BAA5'} headerText={tVars('reactivatedProjectOppN', '{{projectType}} {{projectName}} reactivado', { projectName, projectType })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'reactivatedProjectActivityTab'} />
    )
}

const CustomerProjectOpTurnedProjectComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const projectName = item?.data?.CustomerProject?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faFileArrowUp} mainColor={'#43BAA5'} headerText={tVars('convertedIntoAProjectOpp', 'Oportunidad {{projectName}} convertido a proyecto', { projectName })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName={'turnedProjectActivityTab'} />
    )
}

const CustomerProjectCreatedQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdQuoteOrderN', 'Creación del presupuesto {{quoteOrderNum}}', { quoteOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdQuoteOrder' />
    )
}

const CustomerProjectNewVersionQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('newVersionQuoteOrderN', 'Nueva versión del presupuesto {{quoteOrderNum}}', { quoteOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='newVersionQuoteOrder' />
    )
}

const CustomerProjectWaitingResponseQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseQuoteOrderNum', 'Esperando respuesta del presupuesto {{quoteOrderNum}}', { quoteOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseQuoteOrder' />
    )
}

const CustomerProjectAcceptedQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('acceptedQuoteOrderNum2', 'Presupuesto {{quoteOrderNum}} aceptado', { quoteOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='acceptedQuoteOrder' />
    )
}

const CustomerProjectDeniedQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('deniedQuoteOrderNum', 'Presupuesto {{quoteOrderNum}} denegado', { quoteOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='deniedQuoteOrder' />
    )
}

const CustomerProjectNewVersionCustomerQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    const version = item.data.Order?.version
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdVersionCustomerN', '{{customerName}} creó la versión {{version}} del presupuesto {{quoteOrderNum}}', { quoteOrderNum, version, customerName })} image={item.data.Order?.Customer?.image} date={item?.date} nameTrigger={customerName} tooltipName='newVersionCustomerQuoteOrder' showImage={false} />
    )
}

const CustomerProjectAcceptedCustomerQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('acceptedCustomerN', '{{customerName}} marcó el presupuesto {{quoteOrderNum}} como aceptado', { quoteOrderNum, customerName })} image={item.data.Order?.Customer?.image} date={item.date} nameTrigger={customerName} tooltipName='acceptedCustomerQuoteOrder' showImage={false} />
    )
}

const CustomerProjectDeniedCustomerQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('deniedCustomerN', '{{customerName}} marcó el presupuesto {{quoteOrderNum}} como denegado', { quoteOrderNum, customerName })} image={item.data.Order?.Customer?.image} date={item.date} nameTrigger={customerName} tooltipName='deniedCustomerQuoteOrder' showImage={false} />
    )
}

const CustomerProjectSentCustomerQuoteOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const quoteOrderNum = item.data.Order.full_assigned_number
    const customerName = item.data.Order?.Customer?.name
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('sentCustomerN', 'Se envió el presupuesto {{ quoteOrderNum }} a {{ customerName }}', { quoteOrderNum, customerName })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={customerName} tooltipName='sentCustomerQuoteOrder' />
    )
}

const CustomerProjectCreatedSellOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdSellOrderN', 'Creación del pedido {{sellOrderNum}}', { sellOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdSellOrder' />
    )
}

const CustomerProjectProcessedSellOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('processedSellOrderNum', 'Pedido {{sellOrderNum}} procesado', { sellOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='processedSellOrder' />
    )
}

const CustomerProjectWaitingResponseSellOrderComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellOrderNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseSellOrderNum', 'Esperando respuesta del pedido {{sellOrderNum}}', { sellOrderNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseSellOrder' />
    )
}

const CustomerProjectCreatedSellDeliveryNoteComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdSellDeliveryNoteN', 'Creación del albarán {{sellDeliveryNoteNum}}', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdSellDeliveryNote' />
    )
}

const CustomerProjectProcessedSellDeliveryNoteComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('processedSellDeliveryNoteNum', 'Albarán {{sellDeliveryNoteNum}} procesado', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='processedSellDeliveryNote' />
    )
}

const CustomerProjectWaitingResponseSellDeliveryNoteComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseSellDeliveryNoteNum', 'Esperando respuesta del albarán {{sellDeliveryNoteNum}}', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseSellDeliveryNote' />
    )
}

const CustomerProjectRefundedSellDeliveryNoteComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellDeliveryNoteNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('refundedSellDeliveryNoteNum', 'Albarán {{sellDeliveryNoteNum}} devuelto', { sellDeliveryNoteNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='refundedSellDeliveryNote' />
    )
}

const CustomerProjectCreatedSellInvoiceComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('createdSellInvoiceN', 'Creación de la factura {{sellInvoiceNum}}', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdSellInvoice' />
    )
}

const CustomerProjectAcceptedSellInvoiceComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('acceptedSellInvoiceNum', 'factura {{sellInvoiceNum}} aceptada', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='acceptedSellInvoice' />
    )
}

const CustomerProjectWaitingResponseSellInvoiceComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} headerText={tVars('waitingResponseSellInvoiceNum', 'Esperando respuesta de la factura {{sellInvoiceNum}}', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='waitingResponseSellInvoice' />
    )
}

const CustomerProjectDeniedSellInvoiceComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('deniedSellInvoiceNum', 'Factura {{sellInvoiceNum}} denegada', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='deniedSellInvoice' />
    )
}

const CustomerProjectRefundedSellInvoiceComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { tVars } = useTwinTranslation()
    const sellInvoiceNum = item.data.Order.full_assigned_number
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faBagShopping} mainColor={'#BA435D'} headerText={tVars('refundedSellInvoiceNum', 'Factura {{sellInvoiceNum}} devuelta', { sellInvoiceNum })} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='refundedSellInvoice' />
    )
}

const CustomerCreateLogComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={t('createdCustomer', 'Cliente creado')} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='createdCustomerLog' />
    )
}

const CustomerUpdateLogComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={t('updatedCustomer', 'Cliente actualizado')} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='updateCustomerLog' />
    )
}

const CustomerUnactiveLogComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={t('unactivedCustomer', 'Cliente desactivado')} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='unactiveCustomerLog' />
    )
}

const CustomerReactiveLogComponent: React.FC<CustomerComponentProps> = ({ item }) => {
    const { t } = useTwinTranslation()
    return (
        <ActivityDisplayComponent id={item.data.id} icon={faUser} headerText={t('reactivedCustomer', 'Cliente reactivado')} image={item.data.EmployeeTrigger?.profile_image} date={item.date} nameTrigger={item.data.EmployeeTrigger?.fullname_short} tooltipName='reactiveCustomerLog' />
    )
}

interface ActivityDisplayParentProps {
    userPermissions: UserPermissionsSingle
}

export const ActivityDisplayParent: React.FC<ActivityDisplayParentProps> = ({ ...logic }) => {
    return (
        <div className='flex flex-col min-h-350'>
            <div className='regular18 mb-17 customer_notes_title'>
                <TwinTrans transKey='customerActivity'>Actividad del cliente</TwinTrans>
            </div>
            <ActivityDisplayBody {...logic} />
        </div>
    )
}

interface ActivityDisplayBodyProps {
    userPermissions: UserPermissionsSingle
}

export const ActivityDisplayBody: React.FC<ActivityDisplayBodyProps> = ({ userPermissions }) => {
    const { customerNotesCommentsAndLogs, getCustomerNotesCommentsAndLogs, onMySubmit, CustomerId, setVirtualListHeightAutosizedInstance, VirtualListHeightAutosizedInstance } = useActivityDisplayBodyLogic()
    return (
        <Fragment>
            <VirtualListHeightAutosized extraProps={{ getData: getCustomerNotesCommentsAndLogs, VirtualListHeightAutosizedInstance }} items={customerNotesCommentsAndLogs || []} Component={CustomerActivityItem} className='pt-10' classNameTable='activity_display_list' setVirtualListHeightAutosizedInstance={setVirtualListHeightAutosizedInstance} />
            <PermissionChecker userPermissions={userPermissions} permission={'update'}>
                <CreateTwinComment createURL={'/api/app/customer/customerNote/createCustomerNote'} imagePath={['customer', String(CustomerId), 'customerNotes']} idParent={CustomerId || ''} fieldIdParent='CustomerId' myOnSubmit={onMySubmit} fieldName='message' className='customer_activity_create_comment' />
            </ PermissionChecker>
        </Fragment>
    )
}

const useActivityDisplayBodyLogic = () => {
    const {t} = useTwinTranslation()
    const [customerNotesCommentsAndLogs, setCustomerNotesCommentsAndLogs] = useState<CustomerActivityTy[] | null>(null)
    const [VirtualListHeightAutosizedInstance, setVirtualListHeightAutosizedInstance] = useState<VirtualListHeightAutosizedInstanceType>(null)
    const { id } = useParams()
    const oppTranslation = t('opportunity', 'Oportunidad')
    const projectTranslation = t('project', 'Proyecto')
    const getCustomerNotesCommentsAndLogs = useCallback(async () => {
        const projectTypeDict: Record<ProjectTypesKeys, string> = {
            0: oppTranslation,
            1: projectTranslation
        }
        const result = await twinFetchPostJSON('/api/app/customer/customerNote/getAllCustomerNotesAndLogs', { 'CustomerId': id })
        if (result) {
            const customerActivityData: CustomerActivityTy[] = []
            const customerProjectComments = result?.CustomerProjectComments || []
            for (const element of customerProjectComments) {
                customerActivityData.push({
                    id: 'comment_'+element.id,
                    type: 'comment',
                    data: element,
                    date: element.createdAt
                })
            }
            const customerComments = result?.CustomerComments || []
            for (const element of customerComments) {
                customerActivityData.push({
                    id: 'customerComments' + element.id,
                    type: 'customerComments',
                    data: element,
                    date: element.createdAt
                })
            }
            const customerCommentsCampaigns = result?.CustomerCommentsCampaigns || []
            for (const element of customerCommentsCampaigns) {
                customerActivityData.push({
                    id: 'customerCommentsCampaigns' + element.id,
                    type: 'customerCommentsCampaigns',
                    data: element,
                    date: element.createdAt
                })
            }
            const customerCampaignStatusHistories = result?.CustomerCampaignStatusHistories || []
            for (const element of customerCampaignStatusHistories) {
                customerActivityData.push({
                    id: 'customerCampaignStatus' + element.id,
                    type: 'customerCampaignStatus',
                    data: element,
                    date: element.createdAt
                })
            }
            const customerProjectStatusHistories = result?.CustomerProjectLogs?.CustomerProjectStatusHistories || []
            for (const element of customerProjectStatusHistories) {
                if (element.CustomerProjectStatus) {
                    customerActivityData.push({
                        id: 'status' + element.id,
                        type: 'status',
                        data: { ...element, projectTypeDict },
                        date: element.createdAt
                    })
                }
            }
            const customerProjectEmployees = result?.CustomerProjectLogs?.CustomerProjectEmployeeLogs || []
            for (const element of customerProjectEmployees) {
                customerActivityData.push({
                    id: (element.deleted ? 'deletedEmployee' : 'assignedEmployee') + element.id,
                    type: element.deleted ? 'deletedEmployee' : 'assignedEmployee',
                    data: { ...element, projectTypeDict },
                    date: element.createdAt || ''
                })
            }
            const customerProjectGroups = result?.CustomerProjectLogs?.CustomerProjectGroupLogs || []
            for (const element of customerProjectGroups) {
                customerActivityData.push({
                    id: (element.deleted ? 'deletedGroup' : 'assignedGroup') + element.id,
                    type: element.deleted ? 'deletedGroup' : 'assignedGroup',
                    data: { ...element, projectTypeDict },
                    date: element.createdAt || ''
                })
            }
            const customerProjectChangedTypes = result?.CustomerProjectLogs?.CustomerProjectChangedTypeLogs || []
            for (const element of customerProjectChangedTypes) {
                if (!element.CustomerProjectTypeId) {
                    customerActivityData.push({
                        id: 'turnedProject' + element.id,
                        type: 'turnedProject',
                        data: element,
                        date: element.createdAt || ''
                    })
                } else {
                    customerActivityData.push({
                        id: 'changedType'+element.id,
                        type: 'changedType',
                        data: { ...element, projectTypeDict },
                        date: element.createdAt || ''
                    })
                }
            }
            const customerProjectActives = result?.CustomerProjectLogs?.CustomerProjectActiveLogs || []
            for (const element of customerProjectActives) {
                customerActivityData.push({
                    id: (element.active ? 'reactivatedProject' : 'disabledProject') + element.id,
                    type: element.active ? 'reactivatedProject' : 'disabledProject',
                    data: { ...element, projectTypeDict },
                    date: element.createdAt || ''
                })
            }

            const quoteOrderLogType: Record<QuoteOrderLogTypeKeys, CustomerActivityTypeTy> = {
                0: 'creatingQuoteOrder',
                1: 'newVersionQuoteOrder',
                2: 'waitingResponseQuoteOrder',
                3: 'acceptedQuoteOrder',
                4: 'deniedQuoteOrder',
                5: 'newVersionCustomerQuoteOrder',
                6: 'acceptedCustomerQuoteOrder',
                7: 'deniedCustomerQuoteOrder',
                8: 'sent',
                9: 'processed'
            }

            const quoteOrdersLogs = result?.CustomerQuoteOrderLogs || []
            for (const quoteOrderLog of quoteOrdersLogs) {
                customerActivityData.push({
                    id: quoteOrderLogType[quoteOrderLog.logType as QuoteOrderLogTypeKeys] + quoteOrderLog.id,
                    type: quoteOrderLogType[quoteOrderLog.logType as QuoteOrderLogTypeKeys],
                    data: quoteOrderLog,
                    date: quoteOrderLog.createdAt || ''
                })
            }

            const sellOrderLogType: Record<SellOrderLogTypeKeys, CustomerActivityTypeTy> = {
                0: 'creatingSellOrder',
                1: 'waitingResponseSellOrder',
                2: 'processedSellOrder'
            }

            const sellOrdersLogs = result?.CustomerSellOrderLogs || []
            for (const sellOrderLog of sellOrdersLogs) {
                customerActivityData.push({
                    id: sellOrderLogType[sellOrderLog.logType as SellOrderLogTypeKeys] + sellOrderLog.id,
                    type: sellOrderLogType[sellOrderLog.logType as SellOrderLogTypeKeys],
                    data: sellOrderLog,
                    date: sellOrderLog.createdAt || ''
                })
            }

            const sellDeliveryNoteLogType: Record<SellDeliveryNoteLogTypeKeys, CustomerActivityTypeTy> = {
                0: 'creatingSellDeliveryNote',
                1: 'waitingResponseSellDeliveryNote',
                2: 'processedSellDeliveryNote',
                3: 'refundedSellDeliveryNote'
            }

            const sellDeliveryNoteLogs = result?.CustomerSellDeliveryNoteLogs || []
            for (const sellDeliveryNoteLog of sellDeliveryNoteLogs) {
                customerActivityData.push({
                    id: sellDeliveryNoteLogType[sellDeliveryNoteLog.logType as SellDeliveryNoteLogTypeKeys] + sellDeliveryNoteLog.id,
                    type: sellDeliveryNoteLogType[sellDeliveryNoteLog.logType as SellDeliveryNoteLogTypeKeys],
                    data: sellDeliveryNoteLog,
                    date: sellDeliveryNoteLog.createdAt || ''
                })
            }

            const sellInvoiceLogType: Record<SellInvoiceLogTypeKeys, CustomerActivityTypeTy> = {
                0: 'creatingSellInvoice',
                1: 'waitingResponseSellInvoice',
                2: 'acceptedSellInvoice',
                3: 'deniedSellInvoice',
                4: 'refundedSellInvoice'
            }

            const sellInvoiceLogs = result?.CustomerSellInvoiceLogs || []
            for (const sellInvoiceLog of sellInvoiceLogs) {
                customerActivityData.push({
                    id: sellInvoiceLogType[sellInvoiceLog.logType as SellInvoiceLogTypeKeys] + sellInvoiceLog.id,
                    type: sellInvoiceLogType[sellInvoiceLog.logType as SellInvoiceLogTypeKeys],
                    data: sellInvoiceLog,
                    date: sellInvoiceLog.createdAt || ''
                })
            }

            const customerLogType: Record<CustomerLogTypeKeys, CustomerActivityTypeTy> = {
                0: 'createCustomer',
                1: 'updateCustomer',
                2: 'unactiveCustomer',
                3: 'reactiveCustomer',
            }

            const customerLogs = result?.CustomerLogs || []
            for (const log of customerLogs) {
                customerActivityData.push({
                    id: customerLogType[log.logType as CustomerLogTypeKeys] + log.id,
                    type: customerLogType[log.logType as CustomerLogTypeKeys],
                    data: log,
                    date: log.createdAt || ''
                })
            }

            customerActivityData.sort((a, b) => {
                return new Date(b.date) > new Date(a.date) ? 1 : -1
            })
            setCustomerNotesCommentsAndLogs(customerActivityData)
        }
    }, [id, setCustomerNotesCommentsAndLogs, oppTranslation, projectTranslation])

    const onMySubmit = useCallback(() => {
        const activity = document.querySelector('.activity_display_list');
        if (activity) {
            activity.scrollTop = 0
        }

        getCustomerNotesCommentsAndLogs()
    }, [getCustomerNotesCommentsAndLogs])

    useEffect(() => {
        getCustomerNotesCommentsAndLogs()
    }, [getCustomerNotesCommentsAndLogs])

    return { customerNotesCommentsAndLogs, getCustomerNotesCommentsAndLogs, onMySubmit, CustomerId: id, VirtualListHeightAutosizedInstance, setVirtualListHeightAutosizedInstance }
}